import React, { useState, useEffect } from 'react'
import { AlertCircle, Search, Filter } from 'lucide-react'
import PatientList from '../components/PatientList'
import AlertsPanel from '../components/AlertsPanel'
import QuickStats from '../components/QuickStats'
import { mdApi } from '../api/mdApi'
import { useAuth } from '../context/AuthContext'
import './Dashboard.css'

function Dashboard() {
  const { role } = useAuth()
  const isClinician = role === 'clinician'
  const [patients, setPatients] = useState([])
  const [alerts, setAlerts] = useState([])
  const [stats, setStats] = useState(null)
  const [loading, setLoading] = useState(true)
  const [searchQuery, setSearchQuery] = useState('')
  const [riskFilter, setRiskFilter] = useState('')

  useEffect(() => {
    loadDashboardData()
  }, [])

  const loadDashboardData = async () => {
    try {
      setLoading(true)
      const [patientsData, alertsData, statsData] = await Promise.all([
        mdApi.getPatients(),
        mdApi.getAlerts({ acknowledged: false }),
        isClinician ? mdApi.getPopulationHealth() : Promise.resolve(null)
      ])
      
      setPatients(patientsData)
      setAlerts(alertsData)
      setStats(statsData)
    } catch (error) {
      console.error('Error loading dashboard data:', error)
    } finally {
      setLoading(false)
    }
  }

  const handleSearch = async () => {
    try {
      const filters = {}
      if (searchQuery) filters.search = searchQuery
      if (riskFilter) filters.risk_level = riskFilter
      
      const data = await mdApi.getPatients(filters)
      setPatients(data)
    } catch (error) {
      console.error('Error searching patients:', error)
    }
  }

  const handleAcknowledgeAlert = async (alertId) => {
    try {
      await mdApi.acknowledgeAlert(alertId)
      // Refresh alerts
      const alertsData = await mdApi.getAlerts({ acknowledged: false })
      setAlerts(alertsData)
    } catch (error) {
      console.error('Error acknowledging alert:', error)
    }
  }

  if (loading) {
    return (
      <div className="loading">
        <div className="spinner"></div>
        <p>Loading dashboard...</p>
      </div>
    )
  }

  return (
    <div className="dashboard">
      <div className="dashboard-header">
        <div>
          <h1 className="dashboard-title">Patient Dashboard</h1>
          <p className="dashboard-subtitle">
            {patients.length} patients • {alerts.length} active alerts
          </p>
        </div>
      </div>

      {isClinician && stats && <QuickStats stats={stats} />}

      <div className="dashboard-content">
        <div className="dashboard-main">
          <div className="search-section">
            <div className="search-bar">
              <Search size={20} className="search-icon" />
              <input
                type="text"
                placeholder="Search patients by name or ID..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                className="search-input"
              />
            </div>
            
            <div className="filter-controls">
              <Filter size={16} />
              <select
                value={riskFilter}
                onChange={(e) => {
                  setRiskFilter(e.target.value)
                  handleSearch()
                }}
                className="filter-select"
              >
                <option value="">All Risk Levels</option>
                <option value="critical">Critical</option>
                <option value="high">High</option>
                <option value="medium">Medium</option>
                <option value="low">Low</option>
              </select>
            </div>
          </div>

          <PatientList patients={patients} />
        </div>

        <div className="dashboard-sidebar">
          <AlertsPanel 
            alerts={alerts} 
            onAcknowledge={handleAcknowledgeAlert}
          />
        </div>
      </div>
    </div>
  )
}

export default Dashboard